import axios from 'axios';
import { logError } from '../lib/errorLogger';

const API_URL = import.meta.env.VITE_API_URL || '';
const SESSION_KEY = 'planetseed_session';

export interface GameSession {
  token: string;
  userId: string;
  role: 'student' | 'teacher' | 'admin' | string;
  universeId?: string;
  planetId?: string;
  expiresAt?: string;
}

const getDevUserId = () => localStorage.getItem('dev_user_id') || '';
const getUniverseId = () => getGameSession()?.universeId || localStorage.getItem('current_universe_id') || '';

export function getGameSession(): GameSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) as GameSession : null;
  } catch {
    localStorage.removeItem(SESSION_KEY);
    return null;
  }
}

export const apiClient = axios.create({
  baseURL: `${API_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

apiClient.interceptors.request.use((config) => {
  const session = getGameSession();
  if (session?.token) {
    config.headers.Authorization = `Bearer ${session.token}`;
  } else {
    const userId = getDevUserId();
    if (userId) {
      config.headers['x-user-id'] = userId;
    }
  }
  const universeId = getUniverseId();
  if (universeId) {
    config.headers['x-universe-id'] = universeId;
  }
  const adminToken = localStorage.getItem('admin_token');
  if (adminToken) {
    config.headers['x-admin-token'] = adminToken;
  }
  const devDayDuration = localStorage.getItem('dev_day_duration_ms');
  if (devDayDuration) {
    config.headers['x-dev-day-duration'] = devDayDuration;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url;
    const method = error.config?.method?.toUpperCase();

    if (error.response) {
      logError('api-error', `API ${method} ${url} failed with ${status}`, {
        status,
        url,
        method,
        data: error.response?.data,
        message: error.response?.data?.error?.message || error.message,
      });
    } else if (error.request) {
      logError('api-error', `API ${method} ${url} network error - no response`, {
        url,
        method,
        message: error.message,
      });
    }

    if (status === 401) {
      console.error('Authentication required');
    }
    if (status === 500) {
      console.error('Server error:', error.response?.data);
    }

    return Promise.reject(error);
  }
);

export function setDevUserId(userId: string) {
  localStorage.removeItem(SESSION_KEY);
  localStorage.setItem('dev_user_id', userId);
}

export function setGameSession(session: GameSession): void;
export function setGameSession(userId: string, universeId?: string): void;
export function setGameSession(sessionOrUserId: GameSession | string, universeId?: string) {
  localStorage.removeItem('dev_user_id');
  if (typeof sessionOrUserId === 'string') {
    localStorage.setItem('dev_user_id', sessionOrUserId);
    if (universeId) {
      localStorage.setItem('current_universe_id', universeId);
    }
    return;
  }

  localStorage.setItem(SESSION_KEY, JSON.stringify(sessionOrUserId));
  if (sessionOrUserId.universeId) {
    localStorage.setItem('current_universe_id', sessionOrUserId.universeId);
  } else {
    localStorage.removeItem('current_universe_id');
  }
}

export function clearGameSession() {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem('dev_user_id');
  localStorage.removeItem('current_universe_id');
}
